import type { MimirVideoItemRef } from '../../mimirItemRef';
import type { TimelineInternalOps } from '../../timelineInternalOps';
import type { AddVideoItemsToMainTrackAction } from '../timelineActions';
import type { VideoSourceRef } from '../../sourceRef';
import { subtractFractions } from '@mimir/common/util/integerMath';
import { UndoHelper } from '../../utils/undoHelper';
import { getUniqueId } from '../actionUtils';
import type { ModelAudioBox, ModelVideoBox } from '../../timelineModel';


export function addVideoItemsToMainTrack(timelineInternalHelper: TimelineInternalOps, action: AddVideoItemsToMainTrackAction) {
  const undoHelper = new UndoHelper();

  for (const item of action.videoItems) {
    // First, we need to make sure we have a reference to this item,
    // adding one if needed
    const mimirItemRef: MimirVideoItemRef = {
      itemId: item.itemId,
      type: 'video',
      nAudioChannels: item.nAudioChannels,
      durationInSeconds: item.durationInSeconds,
    };
    undoHelper.add(timelineInternalHelper.addMimirItemRef(mimirItemRef));

    // Then, let's create a new source reference
    const sourceId = getUniqueId(undoHelper, timelineInternalHelper);
    const currentTrackDuration = timelineInternalHelper.getMainTrackDurationInSeconds(null);

    const sourceRef: VideoSourceRef = {
      itemRef: mimirItemRef,
      srcId: sourceId,
      mediaStartOffset: subtractFractions(currentTrackDuration, item.inPointInSeconds),
    };

    undoHelper.add(timelineInternalHelper.addSourceRef(sourceRef));

    const boxId = getUniqueId(undoHelper, timelineInternalHelper);
    const videoBox: ModelVideoBox = {
      type: 'video',
      sourceRef,
      boxId,
      inPointInSeconds: item.inPointInSeconds,
      outPointInSeconds: item.outPointInSeconds,
    };
    undoHelper.add(timelineInternalHelper.addVideoTrackBox(0, videoBox));

    const nAudioBoxesToAdd = Math.min(item.nAudioChannels, timelineInternalHelper.getNumberOfAudioTracks());
    for (let channel = 0; channel < nAudioBoxesToAdd; channel++) {
      const audioBoxId = getUniqueId(undoHelper, timelineInternalHelper);
      const audioBox: ModelAudioBox = {
        type: 'audio',
        sourceRef,
        boxId: audioBoxId,
        inPointInSeconds: item.inPointInSeconds,
        outPointInSeconds: item.outPointInSeconds,
        channel,
      };
      undoHelper.add(timelineInternalHelper.addAudioTrackBox(channel, audioBox));
    }
  }

  action.undo = () => undoHelper.undo();
}

